/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useSessionStore } from '../../session/store/useSessionStore';
import { usePendingOrdersCount } from '../../providers/PendingOrdersCountProvider';
import { useSupportUnreadCount } from '../../providers/SupportUnreadProvider';
import { useHasBusinessCapability } from '../../session/hooks/useEffectiveBusinessCapabilities';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import { useDay1Path } from '../../../onboarding/application/useDay1Path';
import {
  getMergedActiveCashSession,
  subscribeCashSessionChangeSignalDebounced,
  type CashSessionRow,
} from '../../../cash/infra-api';
import { countStockByBranch } from '../../../inventory/stock/infra/queries/stockQueries';
import { summarizePurchasesForBranch, pullPurchasesWhenNeeded } from '../../../purchases/public-api';
import { useOrgTrustStatus } from '../../banners/hooks/useOrgTrustStatus';
import {
  fetchVisibleAppAnnouncements,
  type AppAnnouncement,
} from '../../home/fetchAppAnnouncements';
import {
  getHomeSnapshot,
  type HomeSnapshotAgenda,
} from '../../home/homeSnapshotRepo';
import { useScreenLoad } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { homeDbg, homeDbgStartPointerProbe, homeDbgTimed } from '../../../shared/dev/homeDebug';
import { withHomeRemoteTimeout } from './homeScreenLoadHelpers';
import { fetchComboLowSellableCount } from '../../home/fetchComboLowSellableCount';

export type AgendaToday = HomeSnapshotAgenda;

export type HomeScreenLoadState = {
  salesTotal: number | null;
  salesCount: number | null;
  loadingSales: boolean;
  lowStockCount: number | null;
  comboLowSellableCount: number | null;
  expiringStockCount: number | null;
  expiredStockCount: number | null;
  purchasesToReceiveCount: number | null;
  pendingTasksCount: number | null;
  activeCash: CashSessionRow | null;
  cashLoaded: boolean;
  agendaToday: AgendaToday | null;
  announcements: AppAnnouncement[];
  initialLoaded: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  setAnnouncements: React.Dispatch<React.SetStateAction<AppAnnouncement[]>>;
  orgTrust: ReturnType<typeof useOrgTrustStatus>;
  supportUnreadCount: number | null;
  refreshSupportUnread: (opts?: { force?: boolean }) => Promise<void>;
  pendingOrders: number | null;
  day1: ReturnType<typeof useDay1Path>;
  hasInventory: boolean;
  hasProductCombos: boolean;
  hasAppointments: boolean;
  cashModuleAllowed: boolean;
  currentBranchId: string | null;
  bookingOnly: boolean;
  userId: string | undefined;
};

/**
 * Hook que encapsula toda la carga remota de HomeScreen.
 * El read model remoto es una sola RPC (`home_snapshot`): ventas del día,
 * badges, storefront, agenda y tareas. Caja queda en el path merged
 * (autoridad remota online / local offline, con cache propio); stock y
 * compras leen snapshots RAM locales.
 */
export function useHomeScreenLoad(): HomeScreenLoadState {
  const userId = useAuthStore((s) => s.session?.user?.id);
  const currentBranchId = useSessionStore((s) => s.currentBranchId) ?? null;
  const navCtx = useNavVisibilityContext();
  const { count: pendingOrders } = usePendingOrdersCount();
  const { count: supportUnreadCount, refresh: refreshSupportUnread } = useSupportUnreadCount();
  const hasInventory = useHasBusinessCapability('inventory');
  const hasProductCombos = useHasBusinessCapability('product_combos');
  const hasAppointments = useHasBusinessCapability('appointments');
  const cashModuleAllowed = navCtx.allowedModules.has('Cash');
  const bookingOnly =
    Boolean(navCtx.effectiveCapabilities?.has('appointments')) &&
    !navCtx.effectiveCapabilities?.has('delivery_fulfillment');
  const orgTrust = useOrgTrustStatus();

  const [salesTotal, setSalesTotal] = useState<number | null>(null);
  const [salesCount, setSalesCount] = useState<number | null>(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [lowStockCount, setLowStockCount] = useState<number | null>(null);
  const [comboLowSellableCount, setComboLowSellableCount] = useState<number | null>(null);
  const [expiringStockCount, setExpiringStockCount] = useState<number | null>(null);
  const [expiredStockCount, setExpiredStockCount] = useState<number | null>(null);
  const [purchasesToReceiveCount, setPurchasesToReceiveCount] = useState<number | null>(null);
  const [pendingTasksCount, setPendingTasksCount] = useState<number | null>(null);
  const [activeCash, setActiveCash] = useState<CashSessionRow | null>(null);
  const [cashLoaded, setCashLoaded] = useState(false);
  const [agendaToday, setAgendaToday] = useState<AgendaToday | null>(null);
  const [announcements, setAnnouncements] = useState<AppAnnouncement[]>([]);
  const initialLoadedRef = useRef(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const day1 = useDay1Path({
    userId,
    cashModuleAllowed,
    cashSessionOpen: Boolean(activeCash),
    cashLoaded,
    operationallyActive:
      (salesCount ?? 0) > 0 ||
      (pendingOrders ?? 0) > 0 ||
      (lowStockCount ?? 0) > 0 ||
      (expiringStockCount ?? 0) > 0 ||
      (expiredStockCount ?? 0) > 0 ||
      (purchasesToReceiveCount ?? 0) > 0,
  });

  // Caja en paralelo al load de Home: day1 espera cashLoaded; si el focus invalida
  // loadHomeData a mitad, la card del día 1 nunca aparece hasta el próximo focus.
  // El refresh por foco queda en loadHomeData (soft — el cache remoto de caja
  // deduplica); los cambios de sesión llegan por subscribeCashSessionChangeSignal.
  useEffect(() => {
    if (!cashModuleAllowed) {
      setActiveCash(null);
      setCashLoaded(true);
      return;
    }
    if (!currentBranchId) {
      setActiveCash(null);
      setCashLoaded(false);
      return;
    }
    let cancelled = false;
    setCashLoaded(false);
    void getMergedActiveCashSession(currentBranchId)
      .then((cash) => {
        if (cancelled) return;
        setActiveCash(cash);
        setCashLoaded(true);
      })
      .catch((error) => {
        captureCatch('shell', 'home-cash-bootstrap')(error);
        if (cancelled) return;
        setActiveCash(null);
        setCashLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [cashModuleAllowed, currentBranchId]);

  const loadGenerationRef = useRef(0);
  const loadingSalesGenerationRef = useRef(0);
  const skipHomeHeavyRemoteRef = useRef(day1.skipHomeHeavyRemoteLoad);
  skipHomeHeavyRemoteRef.current = day1.skipHomeHeavyRemoteLoad;

  const loadHomeDataRef = useRef<(() => Promise<void>) | null>(null);

  const clearLoadingSalesForGeneration = useCallback((generation: number) => {
    if (loadingSalesGenerationRef.current === generation) {
      setLoadingSales(false);
    }
  }, []);

  const loadHomeData = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    const stale = () => generation !== loadGenerationRef.current;
    homeDbg('Home.load', 'begin', {
      generation,
      branchId: currentBranchId ?? null,
      skipHeavy: skipHomeHeavyRemoteRef.current,
    });

    try {
      if (!stale()) {
        const rows = await homeDbgTimed('Home.load', 'announcements', () =>
          withHomeRemoteTimeout(fetchVisibleAppAnnouncements(userId), []),
        );
        if (!stale()) setAnnouncements(rows);
      }
      if (stale()) {
        homeDbg('Home.load', 'stale after announcements', { generation });
        return;
      }
      void orgTrust.refresh().catch(captureCatch('shell', 'home-org-trust-refresh'));
      // Sin force: el provider lee el mismo home_snapshot cacheado — el force
      // del load de abajo lo comparte vía in-flight dedupe.
      void refreshSupportUnread();

      if (!currentBranchId) {
        homeDbg('Home.load', 'no branch — reset state');
        if (!stale()) {
          setSalesTotal(null);
          setSalesCount(null);
          setLowStockCount(null);
          setExpiringStockCount(null);
          setExpiredStockCount(null);
          setPurchasesToReceiveCount(null);
          setPendingTasksCount(null);
          setActiveCash(null);
          setCashLoaded(false);
          setAgendaToday(null);
        }
        return;
      }

      const skipHeavyRemote = skipHomeHeavyRemoteRef.current;
      homeDbg('Home.load', 'skipHeavyRemote', { skipHeavyRemote });

      if (!stale()) {
        loadingSalesGenerationRef.current = generation;
        setLoadingSales(true);
      }
      try {
        // force solo en reloads: en el primer mount el cache está vacío o los
        // providers ya tienen el fetch in-flight — forzar acá duplicaba la RPC.
        const snap = await homeDbgTimed('Home.load', 'homeSnapshot', () =>
          withHomeRemoteTimeout(
            getHomeSnapshot(currentBranchId, { force: initialLoadedRef.current }),
            null,
          ),
        );
        if (!stale()) {
          setSalesTotal(snap?.salesToday?.total ?? null);
          setSalesCount(snap?.salesToday?.count ?? null);
          setPendingTasksCount(
            navCtx.allowedModules.has('Customers') ? snap?.pendingTasks ?? null : null,
          );
          setAgendaToday(hasAppointments ? snap?.agendaToday ?? null : null);
        }
      } catch {
        if (!stale()) {
          setSalesTotal(null);
          setSalesCount(null);
          setPendingTasksCount(null);
          setAgendaToday(null);
        }
      } finally {
        clearLoadingSalesForGeneration(generation);
      }

      if (stale()) {
        homeDbg('Home.load', 'stale after snapshot', { generation });
        return;
      }

      // Refresh soft de caja en foco: remoto TTL-cacheado online, local offline.
      if (cashModuleAllowed) {
        void getMergedActiveCashSession(currentBranchId)
          .then((cash) => {
            if (!stale()) setActiveCash(cash);
          })
          .catch(captureCatch('shell', 'home-cash-refresh'));
      }

      if (skipHeavyRemote) {
        homeDbg('Home.load', 'skip heavy remote — done');
        if (!stale()) {
          setLowStockCount(null);
          setComboLowSellableCount(null);
          setExpiringStockCount(null);
          setExpiredStockCount(null);
          setPurchasesToReceiveCount(null);
        }
        return;
      }

      if (hasInventory && navCtx.allowedModules.has('Inventory')) {
        try {
          const [lowCount, expiringCount, expiredCount] = await homeDbgTimed('Home.load', 'stockCounts', () =>
            Promise.all([
              withHomeRemoteTimeout(
                countStockByBranch(currentBranchId, { statusFilter: 'low' }),
                0,
              ),
              withHomeRemoteTimeout(
                countStockByBranch(currentBranchId, { statusFilter: 'expiring' }),
                0,
              ),
              withHomeRemoteTimeout(
                countStockByBranch(currentBranchId, { statusFilter: 'expired' }),
                0,
              ),
            ]),
          );
          if (!stale()) {
            setLowStockCount(lowCount);
            setExpiringStockCount(expiringCount);
            setExpiredStockCount(expiredCount);
          }
        } catch {
          if (!stale()) {
            setLowStockCount(null);
            setExpiringStockCount(null);
            setExpiredStockCount(null);
          }
        }
      } else if (!stale()) {
        setLowStockCount(null);
        setExpiringStockCount(null);
        setExpiredStockCount(null);
      }

      if (hasProductCombos && hasInventory && navCtx.allowedModules.has('Inventory')) {
        try {
          const comboLow = await homeDbgTimed('Home.load', 'comboLowSellable', () =>
            withHomeRemoteTimeout(fetchComboLowSellableCount(currentBranchId), null),
          );
          if (!stale()) setComboLowSellableCount(comboLow);
        } catch {
          if (!stale()) setComboLowSellableCount(null);
        }
      } else if (!stale()) {
        setComboLowSellableCount(null);
      }

      if (navCtx.allowedModules.has('Purchases')) {
        try {
          // El summary lee el snapshot RAM: en fresh launch está vacío y la card
          // "por recibir" nunca aparece. Disparar el pull (debounced) primero.
          await homeDbgTimed('Home.load', 'purchases.pull', () =>
            withHomeRemoteTimeout(pullPurchasesWhenNeeded(currentBranchId), {
              pulled: false,
            }),
          );
          const summary = await homeDbgTimed('Home.load', 'purchases', () =>
            withHomeRemoteTimeout(summarizePurchasesForBranch(currentBranchId), {
              totalOrders: 0,
              totalAmount: 0,
              pendingReception: 0,
              partialReception: 0,
              received: 0,
              cancelled: 0,
              toPay: 0,
              toPayAmount: 0,
              overdue: 0,
            }),
          );
          if (!stale()) setPurchasesToReceiveCount(summary.pendingReception);
        } catch {
          if (!stale()) setPurchasesToReceiveCount(null);
        }
      } else if (!stale()) {
        setPurchasesToReceiveCount(null);
      }

      homeDbg('Home.load', 'complete', { generation });
    } finally {
      clearLoadingSalesForGeneration(generation);
      if (!initialLoadedRef.current) {
        initialLoadedRef.current = true;
        setInitialLoaded(true);
      }
    }
  }, [
    cashModuleAllowed,
    clearLoadingSalesForGeneration,
    currentBranchId,
    hasAppointments,
    hasInventory,
    navCtx.allowedModules,
    orgTrust.refresh,
    refreshSupportUnread,
    userId,
  ]);

  loadHomeDataRef.current = loadHomeData;

  const stableLoadHomeData = useCallback(async () => {
    await loadHomeDataRef.current?.();
  }, []);

  const { refreshing, onRefresh } = useScreenLoad('Home', stableLoadHomeData, { feedback: false });

  // El primer load puede correr con skipHeavyRemote (catálogo sin hidratar) y
  // salta stock/compras; sin re-load, la card "órdenes por recibir" nunca
  // aparece hasta que el usuario re-enfoca Home. Re-cargar al flippear el flag.
  const skipHeavyRemoteNow = day1.skipHomeHeavyRemoteLoad;
  const prevSkipHeavyRemoteRef = useRef(skipHeavyRemoteNow);
  useEffect(() => {
    const wasSkipped = prevSkipHeavyRemoteRef.current;
    prevSkipHeavyRemoteRef.current = skipHeavyRemoteNow;
    if (wasSkipped && !skipHeavyRemoteNow) {
      void loadHomeDataRef.current?.();
    }
  }, [skipHeavyRemoteNow]);

  useFocusEffect(
    useCallback(() => {
      homeDbg('HomeScreen', 'focus');
      void day1.reloadEvidence();
      const stopPointerProbe = homeDbgStartPointerProbe();
      return () => {
        homeDbg('HomeScreen', 'blur');
        stopPointerProbe?.();
        // No bumpear loadGeneration acá: Strict Mode / cambio de deps cancela
        // loadHomeData a mitad y deja Home sin day1 hasta el próximo alt-tab.
        // useScreenLoad vuelve a disparar load al focus; generation se avanza al start.
      };
    }, [day1.reloadEvidence]),
  );

  useEffect(() => {
    if (!cashModuleAllowed || !currentBranchId) return;
    return subscribeCashSessionChangeSignalDebounced(() => {
      void getMergedActiveCashSession(currentBranchId)
        .then(setActiveCash)
        .catch((error) => {
          captureCatch('shell', 'home-cash-session-subscribe')(error);
          setActiveCash(null);
        });
    });
  }, [cashModuleAllowed, currentBranchId]);

  return {
    salesTotal,
    salesCount,
    loadingSales,
    lowStockCount,
    comboLowSellableCount,
    expiringStockCount,
    expiredStockCount,
    purchasesToReceiveCount,
    pendingTasksCount,
    activeCash,
    cashLoaded,
    agendaToday,
    announcements,
    initialLoaded,
    refreshing,
    onRefresh,
    setAnnouncements,
    orgTrust,
    supportUnreadCount,
    refreshSupportUnread,
    pendingOrders,
    day1,
    hasInventory,
    hasProductCombos,
    hasAppointments,
    cashModuleAllowed,
    currentBranchId,
    bookingOnly,
    userId,
  };
}
